import type { TaskDetailData } from "@/lib/board";

/**
 * React key for the comment composer. Includes the task id so navigating between
 * task URLs (back/forward) without unmounting the dialog REMOUNTS the composer —
 * otherwise a draft survives and posts to the wrong task, and its attachment
 * claims (task-scoped) silently vanish. `resetCounter` bumps to clear the
 * composer after a successful post on the same task.
 */
export function commentComposerKey(
  taskId: string | null,
  resetCounter: number,
): string {
  return `${taskId ?? ""}:${resetCounter}`;
}

/** A date with no time, e.g. "Feb 16, 2026". */
export function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Activity timestamps carry the time too, ClickUp-style: "Feb 16 at 2:21 pm". */
export function fmtDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} at ${time}`;
}

/** Whole days elapsed since `iso`, e.g. "3 days". */
export function daysSince(iso: string | null): string {
  if (!iso) return "—";
  return `${Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)} days`;
}

/** Human label for one audit-history row. */
export function describeAction(h: TaskDetailData["history"][number]): string {
  const who = h.actorName ?? "System";
  switch (h.action) {
    case "projected":
      return h.fromStatus ? "Re-entered review" : "Entered review";
    case "accepted":
      return `Accepted by ${who}`;
    case "changes_requested":
      return `Changes requested by ${who}`;
    case "reopened":
      return `Reopened for review by ${who}`;
    case "archived":
      return `Archived by ${who}`;
    case "unarchived":
      return `Restored by ${who}`;
    case "resynced":
      return "Resynced from ClickUp";
    default:
      return h.action;
  }
}
